import React, { useState, useContext } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Modal, Button, Header, Icon,
} from 'semantic-ui-react';
import MessageContext from '../../contexts/MessageContext';
import { MANAGED_PROJECTS, UPCOMING_MEETINGS } from '../../queries/profile';
import { RESCHEDULE_MEETING } from '../../queries/meeting';
import { JOB_MEETING_CONTEXT } from '../../queries/job';
import BlockTimeOption from './BlockTimeOption';

const RescheduleMeeting = ({ job, children }) => {
  const [open, setOpen] = useState(false);
  const [blockTime, setBlockTime] = useState(false);
  const { setSuccessPopup, setErrorPopup } = useContext(MessageContext);

  const {
    id, project, expert, meetingAt,
  } = job;

  // Interviewers are not loaded by PROJECT_JOBS to keep the list-view payload
  // small. Fetch them on demand when this modal opens; Apollo merges the
  // result into the same Job:id entity (and reuses the cache if MeetingModal
  // already fetched them).
  const { data } = useQuery(JOB_MEETING_CONTEXT, {
    skip: !open,
    variables: { id },
  });
  const interviewers = data?.job?.interviewers ?? [];

  const handleOpen = () => {
    setBlockTime(false);
    setOpen(true);
  };

  const [rescheduleMeeting, { loading }] = useMutation(RESCHEDULE_MEETING, {
    refetchQueries: [
      { query: UPCOMING_MEETINGS },
      { query: MANAGED_PROJECTS },
    ],
    onCompleted: () => {
      setSuccessPopup({
        title: `Request to reschedule has been sent to ${expert.fullName}`,
        content: `This event  will be removed from  your calendar and upcoming meetings.
          You will receive a notification when the expert reschedules a time.
          In the meantime, please head over to  the Settings > Availability page if you need to update your availability.`,
        onClose: () => setOpen(false),
      });
    },
    onError: (err) => {
      setErrorPopup({
        title: 'Failed to reschedule this meeting',
        content: err.message,
        onClose: () => setOpen(false),
      });
    },
  });

  const interviewerNames = interviewers.map((i) => i.fullName || i.email);

  return (
    <Modal
      closeIcon={!loading}
      closeOnEscape={!loading}
      closeOnDimmerClick={!loading}
      open={open}
      onOpen={handleOpen}
      onClose={() => !loading && setOpen(false)}
      trigger={children}
      className="setting-modal-layout change-meeting-modal"
    >
      <Modal.Header>Reschedule Meeting</Modal.Header>
      <Modal.Content>
        <Header>
          {`Reschedule ${project.title} with ${expert.fullName}`}
          <Header.Subheader>
            The current meeting will be canceled and the expert
            will receive a link to pick a new time.
          </Header.Subheader>
        </Header>
        {meetingAt && (
          <BlockTimeOption
            meetingAt={meetingAt}
            checked={blockTime}
            onChange={setBlockTime}
            disabled={loading}
            helpText="Leave unchecked to keep this time open. The expert may choose this same time again."
          />
        )}
        <div className="change-warning">
          <Icon name="warning sign" />
          <div>
            <div>
              <strong>{'Rescheduling doesn\'t guarantee a new meeting. '}</strong>
              The expert still needs to pick a time.
            </div>
            {interviewerNames.length > 0 && (
              <div>
                {`Interviewer${interviewerNames.length > 1 ? 's' : ''} affected: ${interviewerNames.join(', ')}`}
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button className="basic-btn" disabled={loading} onClick={() => setOpen(false)}>
          Go back
        </Button>
        <Button
          loading={loading}
          disabled={loading}
          className="primary-btn"
          onClick={() => rescheduleMeeting({ variables: { jobId: id, blockTime } })}
        >
          Reschedule meeting
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default RescheduleMeeting;
